import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import PublicPropertyPage from "@/pages/PublicPropertyPage";
import LandingPage from "@/pages/LandingPage";
import FeaturesPage from "@/pages/FeaturesPage";
import PricingPage from "@/pages/PricingPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import CookiesPage from "@/pages/CookiesPage";
import CookieBanner from "@/components/marketing/CookieBanner";
import BetaBanner from "@/components/BetaBanner";
import MetaPixelRouteTracker from "@/components/MetaPixelRouteTracker";
import { PresentModeProvider, usePresentMode } from "@/contexts/PresentModeContext";

const PROTECTED_PATHS = [
  '/dashboard',
  '/calendar',
  '/calendar-sync',
  '/properties',
  '/files',
  '/bookings',
  '/cleaning',
  '/maintenance',
  '/renovations',
  '/expenses',
  '/workers',
  '/reports',
  '/invoices',
  '/work-orders',
  '/users',
  '/billing',
  '/account',
  '/team-chat',
  '/waitlist',
  '/email-health',
  '/analytics',
];

function AppRouter() {
  const location = useLocation();

  // OAuth callback in URL fragment
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public marketing */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiesPage />} />

      {/* Public property showcase — no auth required */}
      <Route path="/p/:slug" element={<PublicPropertyPage />} />

      {/* Auth pages */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected app routes */}
      {PROTECTED_PATHS.map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
      ))}
    </Routes>
  );
}

function AppChrome() {
  const { presentMode } = usePresentMode();
  return (
    <>
      {!presentMode && <BetaBanner />}
      <MetaPixelRouteTracker />
      <AppRouter />
      {!presentMode && <CookieBanner />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PresentModeProvider>
          <AuthProvider>
            <AppChrome />
          </AuthProvider>
        </PresentModeProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
